// src/context/DeliveryContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { useUser } from "./UserContext";
import {
  subscribeToDeliveries,
  getCompletedDeliveries as fetchCompletedDeliveriesFromDb,
} from "../firebase/deliveries";
import {
  acceptDeliveryFn,
  updateDeliveryStatusFn,
  markDeliveryAsPicked,
  completeDeliveryFn,
  revertDeliveryToActive,
} from "../firebase/deliveryFunctions";

const DeliveryContext = createContext();

export function DeliveryProvider({ children }) {
  const { user: authUser } = useAuth();
  const { userData } = useUser();

  const [allDeliveries, setAllDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authUser?.uid) {
      setAllDeliveries([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    const unsubscribe = subscribeToDeliveries(
      (deliveriesData) => {
        setAllDeliveries(deliveriesData || []);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Error subscribing to deliveries:", err);
        setError(err?.message || "Subscription error");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authUser?.uid]);

  // ============================================================================
  // HELPERS
  // ============================================================================

  const getActiveDeliveries = useCallback(() => {
    return (allDeliveries || []).filter(
      (d) => d?.status === "active" && d?.hide !== true
    );
  }, [allDeliveries]);

  const getUserDeliveriesFromUserDoc = useCallback(() => {
    if (!userData) return [];
    
    const map = userData?.deliveries;

    
    if (!map || typeof map !== "object" || Array.isArray(map)) return [];

    return Object.entries(map).map(([deliveryId, data]) => ({
      id: deliveryId,
      deliveryId,
      ...(data || {}),
    }));
  }, [userData]);

  const getOwnDeliveries = useCallback(() => {
    const list = getUserDeliveriesFromUserDoc();
    return list.filter((d) => d?.status !== "completed");
  }, [getUserDeliveriesFromUserDoc]);

  // Deliveries where WE are the courier, not the sender
  const getOwnDriveDeliveries = useCallback(() => {
    const list = getUserDeliveriesFromUserDoc();
    return list.filter(
      (d) => d?.status !== "completed" && d?.senderId !== authUser?.uid
    );
  }, [getUserDeliveriesFromUserDoc, authUser?.uid]);

  const getOwnCompletedDeliveries = useCallback(() => {
    const list = getUserDeliveriesFromUserDoc();
    return list.filter((d) => d?.status === "completed");
  }, [getUserDeliveriesFromUserDoc]);

  const getDeliveriesByStatus = useCallback(
    (status) => {
      const list = getUserDeliveriesFromUserDoc();
      return list.filter((d) => d?.status === status);
    },
    [getUserDeliveriesFromUserDoc]
  );

  // ============================================================================
  // CALLABLE FUNCTIONS
  // ============================================================================

  const acceptDelivery = useCallback(
    async (deliveryId) => {
      if (!authUser?.uid) throw new Error("Must be authenticated to accept deliveries");
      if (!deliveryId) throw new Error("deliveryId missing");
      await acceptDeliveryFn(deliveryId);
      return true;
    },
    [authUser?.uid]
  );

  const updateStatus = useCallback(async (deliveryId, newStatus) => {
    if (!deliveryId) throw new Error("deliveryId missing");
    if (!newStatus) throw new Error("newStatus missing");
    await updateDeliveryStatusFn(deliveryId, newStatus);
    return true;
  }, []);

  const markAsPicked = useCallback(async (deliveryId) => {
    if (!deliveryId) throw new Error("deliveryId missing");
    await markDeliveryAsPicked(deliveryId);
    return true;
  }, []);

  const completeDelivery = useCallback(async (deliveryId) => {
    if (!deliveryId) throw new Error("deliveryId missing");
    await completeDeliveryFn(deliveryId);
    return true;
  }, []);

  const revertToActive = useCallback(async (deliveryId) => {
    if (!deliveryId) throw new Error("deliveryId missing");
    await revertDeliveryToActive(deliveryId);
    return true;
  }, []);

  const completedInFlightRef = useRef(false);
  const getCompletedDeliveries = useCallback(async () => {
    if (!authUser?.uid) return [];
    if (completedInFlightRef.current) return [];
    completedInFlightRef.current = true;
    try {
      return await fetchCompletedDeliveriesFromDb(authUser.uid);
    } finally {
      completedInFlightRef.current = false;
    }
  }, [authUser?.uid]);

  const value = {
    allDeliveries,
    loading,
    error,
    getActiveDeliveries,
    getOwnDeliveries,
    getOwnDriveDeliveries,
    getOwnCompletedDeliveries,
    getDeliveriesByStatus,
    getUserDeliveriesFromUserDoc,
    acceptDelivery,
    updateStatus,
    markAsPicked,
    completeDelivery,
    revertToActive,
    getCompletedDeliveries,
  };

  return (
    <DeliveryContext.Provider value={value}>
      {children}
    </DeliveryContext.Provider>
  );
}

export function useDelivery() {
  const context = useContext(DeliveryContext);
  if (context === undefined) {
    throw new Error("useDelivery must be used within a DeliveryProvider");
  }
  return context;
}